import {useState, useEffect, useRef, useCallback} from 'react'
import {jwtGetFetcher, jwtPostFetcher} from "../../../utils/authFetch"

// ── EVE status constants ─────────────────────────────────────────────────────

export const EVE_TERMINAL_SUCCESS = ['done', 'completed', 'success', 'succeeded', 'ready']
export const EVE_TERMINAL_ERROR = ['error', 'failed', 'failure', 'not_implemented', 'not implemented']
export const EVE_RETRY_FULL_STATUSES = ['failed', 'received', 'fetching', 'extracting', 'embedding']
export const EVE_POLL_INTERVAL_MS = 2000
export const EVE_MAX_POLL_ATTEMPTS = 60

export const getAiIngestionTimeout = () => (typeof window !== 'undefined' && window.AI_INGESTION_TIMEOUT) || 30000

export const getSummaryStatusPollIntervalMs = (elapsedMs) => {
  if (elapsedMs < 2 * 60 * 1000) return 3000
  if (elapsedMs < 5 * 60 * 1000) return 5000
  if (elapsedMs < 10 * 60 * 1000) return 10000
  return 20000
}

// ── Status helpers ───────────────────────────────────────────────────────────

export const normalizeStatus = (status) => (status || '').toString().trim().toLowerCase()
export const normalizeStatusRaw = (status) => (status || '').toString().trim().toUpperCase()

export const getEveLoadingLabel = (rawStatus) => {
  const status = normalizeStatusRaw(rawStatus)
  const labels = {
    RECEIVED: 'AI has received the record',
    FETCHING: 'Fetching the record',
    EXTRACTING: 'Extracting document information',
    EMBEDDING: 'Preparing AI context',
    INGESTED: 'Document ingested, starting AI features',
    SUMMARIZING: 'Generating summary',
    COMPLETED: 'Summary completed',
    FAILED: 'AI processing failed',
    NOT_IMPLEMENTED: 'This document type is not supported yet',
  }
  return labels[status] || (status || 'Generating summary')
}

export const getSummaryParagraphs = (statusResponse) => {
  const summaryCandidate =
    statusResponse?.summary ||
    statusResponse?.aiSummary ||
    statusResponse?.documentSummary ||
    statusResponse?.result?.summary ||
    statusResponse?.result?.aiSummary ||
    statusResponse?.result?.documentSummary ||
    statusResponse?.content

  if (Array.isArray(summaryCandidate)) {
    return summaryCandidate.filter(Boolean).map((item) => item.toString().trim()).filter(Boolean)
  }

  if (typeof summaryCandidate === 'string') {
    return summaryCandidate
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
  }

  return []
}

export const toPercentScore = (score) => {
  const parsed = Number(score)
  if (!Number.isFinite(parsed)) {
    return null
  }

  const normalized = parsed >= 0 && parsed <= 1 ? parsed * 100 : parsed
  return Math.max(0, Math.min(100, Math.round(normalized)))
}

export const getRelevancyScore = (relevancyResponse) => {
  const score =
    relevancyResponse?.result?.score ??
    relevancyResponse?.score ??
    relevancyResponse?.result?.relevanceScore ??
    relevancyResponse?.relevanceScore ??
    relevancyResponse?.result?.relevance?.score ??
    relevancyResponse?.relevance?.score

  return toPercentScore(score)
}

export const getPatientMismatch = (statusResponse) => {
  const mismatch =
    statusResponse?.patientMismatch ??
    statusResponse?.patient?.mismatch ??
    statusResponse?.result?.patientMismatch ??
    statusResponse?.result?.patient?.mismatch
  return Boolean(mismatch)
}

export const getRelevancyClassification = (relevancyResponse) => {
  return (
    relevancyResponse?.result?.classification ??
    relevancyResponse?.classification ??
    null
  )
}

export const getRelevancyExplanation = (relevancyResponse) => {
  return (
    relevancyResponse?.result?.explanation ??
    relevancyResponse?.explanation ??
    null
  )
}

export const getPatientMatchDetermination = (patientMatchResponse) => {
  return (
    patientMatchResponse?.result?.determination ??
    patientMatchResponse?.determination ??
    null
  )
}

export const getEveDocumentId = (ingestResponse) =>
  ingestResponse?.eveDocumentId ||
  ingestResponse?.documentId ||
  ingestResponse?.id ||
  ingestResponse?.result?.eveDocumentId ||
  ingestResponse?.result?.documentId

// ── useAiSummary hook ────────────────────────────────────────────────────────

export const useAiSummary = ({isExpanded, requestId, recordId, authJwt}) => {
  const [aiState, setAiState] = useState('idle') // idle | loading | done | error
  const [summaryParagraphs, setSummaryParagraphs] = useState([])
  const [summaryData, setSummaryData] = useState(null) // full structured summary response
  const [summaryErrorMessage, setSummaryErrorMessage] = useState('')
  const [relevanceScore, setRelevanceScore] = useState(null)
  const [patientMismatch, setPatientMismatch] = useState(false)
  const [relevancyClassification, setRelevancyClassification] = useState(null)
  const [relevancyExplanation, setRelevancyExplanation] = useState(null)
  const [patientMatchDetermination, setPatientMatchDetermination] = useState(null)
  const [patientMatchExplanation, setPatientMatchExplanation] = useState(null)
  const [patientMatchConfidence, setPatientMatchConfidence] = useState(null)
  const [eveStatus, setEveStatus] = useState('')
  const [aiErrorMessage, setAiErrorMessage] = useState('')
  const [patientMatchError, setPatientMatchError] = useState('')
  const [relevancyError, setRelevancyError] = useState('')
  const [resolvedEveDocumentId, setResolvedEveDocumentId] = useState(null)
  const [retryTrigger, setRetryTrigger] = useState(0)
  const [featuresCancelled, setFeaturesCancelled] = useState(false)
  const [featuresLoading, setFeaturesLoading] = useState(false)
  const [patientMatchLoading, setPatientMatchLoading] = useState(false)
  const [relevancyLoading, setRelevancyLoading] = useState(false)
  const [summaryCancelled, setSummaryCancelled] = useState(false)
  const [canCancel, setCanCancel] = useState(false)
  const [canRetry, setCanRetry] = useState(false)
  const [showFeatureCancelLink, setShowFeatureCancelLink] = useState(false)
  const pollTimeoutRef = useRef(null)
  const isCancelledRef = useRef(false)
  const featuresCancelledRef = useRef(false)
  const eveDocumentIdRef = useRef(null)
  const lastStatusRef = useRef(null)
  const featureCancelTimerRef = useRef(null)

  const clearPolling = useCallback(() => {
    clearTimeout(pollTimeoutRef.current)
    clearTimeout(featureCancelTimerRef.current)
  }, [])

  // Cancel the summary/ingestion flow
  const cancelSummary = useCallback(async () => {
    const docId = eveDocumentIdRef.current
    if (!docId || !authJwt) return
    try {
      await jwtPostFetcher(authJwt)(`/api/eve/documents/${docId}/cancel`)
    } catch (e) {
      console.warn('Cancel request failed:', e?.message)
    }
    isCancelledRef.current = true
    clearPolling()
    setSummaryCancelled(true)
    setFeaturesCancelled(true)
    featuresCancelledRef.current = true
    setFeaturesLoading(false)
    setPatientMatchLoading(false)
    setRelevancyLoading(false)
    setAiState('idle')
    setAiErrorMessage('')
    setCanCancel(false)
  }, [authJwt, clearPolling])

  // Fake cancel for SUMMARIZING state: resets UI without calling /cancel endpoint
  const fakeCancelSummary = useCallback(() => {
    isCancelledRef.current = true
    clearPolling()
    setSummaryCancelled(true)
    setFeaturesCancelled(true)
    featuresCancelledRef.current = true
    setFeaturesLoading(false)
    setPatientMatchLoading(false)
    setRelevancyLoading(false)
    setAiState('idle')
    setAiErrorMessage('')
    setCanCancel(false)
  }, [clearPolling])

  // Cancel feature polling (patient-match + relevancy) without calling an endpoint
  const cancelFeatures = useCallback(() => {
    featuresCancelledRef.current = true
    setFeaturesCancelled(true)
    setFeaturesLoading(false)
    setPatientMatchLoading(false)
    setRelevancyLoading(false)
    setShowFeatureCancelLink(false)
  }, [])

  // Load/reload features (patient-match + relevancy)
  const loadFeatures = useCallback(async () => {
    const docId = resolvedEveDocumentId
    if (!docId || !authJwt) return
    setFeaturesCancelled(false)
    featuresCancelledRef.current = false
    setPatientMatchLoading(true)
    setRelevancyLoading(true)
    setPatientMatchError('')
    setRelevancyError('')
    setPatientMatchDetermination(null)
    setPatientMatchExplanation(null)
    setPatientMatchConfidence(null)
    setPatientMismatch(false)
    setRelevanceScore(null)
    setRelevancyClassification(null)
    setRelevancyExplanation(null)
    setShowFeatureCancelLink(false)

    // Start feature cancel timer
    featureCancelTimerRef.current = setTimeout(() => {
      if (!featuresCancelledRef.current) setShowFeatureCancelLink(true)
    }, getAiIngestionTimeout())

    const waitForNextPollTick = () => new Promise((resolve) => {
      setTimeout(resolve, EVE_POLL_INTERVAL_MS)
    })

    const pollFeature = async (endpoint, featureLabel) => {
      await jwtPostFetcher(authJwt)(endpoint)
      for (let attempt = 0; attempt <= EVE_MAX_POLL_ATTEMPTS; attempt += 1) {
        if (isCancelledRef.current || featuresCancelledRef.current) return null
        const response = await jwtGetFetcher(authJwt)(endpoint)
        const status = normalizeStatus(response?.status || response?.state)
        if (EVE_TERMINAL_SUCCESS.includes(status)) return response
        if (EVE_TERMINAL_ERROR.includes(status)) {
          throw new Error(response?.message || response?.error || `Error loading ${featureLabel}`)
        }
        if (attempt >= EVE_MAX_POLL_ATTEMPTS) throw new Error(`Timed out while waiting for ${featureLabel}.`)
        await waitForNextPollTick()
      }
      throw new Error(`Error loading ${featureLabel}`)
    }

    const pmPromise = pollFeature(`/api/eve/documents/${docId}/patient-match`, 'patient match')
      .then((response) => {
        if (response && !isCancelledRef.current && !featuresCancelledRef.current) {
          const pmDetermination = getPatientMatchDetermination(response)
          const isNoMatch = pmDetermination === 'NO_MATCH'
          setPatientMatchDetermination(pmDetermination)
          setPatientMismatch(isNoMatch || getPatientMismatch(response))
          setPatientMatchExplanation(response?.result?.explanation ?? response?.explanation ?? null)
          setPatientMatchConfidence(toPercentScore(response?.result?.confidenceScore ?? response?.confidenceScore))
          setPatientMatchError('')
        }
        if (!isCancelledRef.current && !featuresCancelledRef.current) setPatientMatchLoading(false)
        return {ok: true}
      })
      .catch((error) => {
        if (!isCancelledRef.current && !featuresCancelledRef.current) {
          setPatientMatchLoading(false)
          setPatientMatchError(error?.message || 'Error loading patient match')
        }
        return {ok: false, error}
      })

    const relPromise = pollFeature(`/api/eve/documents/${docId}/relevancy`, 'relevancy')
      .then((response) => {
        if (response && !isCancelledRef.current && !featuresCancelledRef.current) {
          setRelevanceScore(getRelevancyScore(response))
          setRelevancyClassification(getRelevancyClassification(response))
          setRelevancyExplanation(getRelevancyExplanation(response))
          setRelevancyError('')
        }
        if (!isCancelledRef.current && !featuresCancelledRef.current) setRelevancyLoading(false)
        return {ok: true}
      })
      .catch((error) => {
        if (!isCancelledRef.current && !featuresCancelledRef.current) {
          setRelevancyLoading(false)
          setRelevancyError(error?.message || 'Error loading relevancy')
        }
        return {ok: false, error}
      })

    await Promise.all([pmPromise, relPromise])
    clearTimeout(featureCancelTimerRef.current)
    setShowFeatureCancelLink(false)
  }, [resolvedEveDocumentId, authJwt])

  // Retry the entire flow (calls /retry endpoint then re-polls status)
  const retryAll = useCallback(() => {
    setFeaturesCancelled(false)
    featuresCancelledRef.current = false
    setSummaryCancelled(false)
    setRetryTrigger(v => v + 1)
  }, [])

  // Retry only patient-match
  const retryPatientMatch = useCallback(async () => {
    const docId = resolvedEveDocumentId
    if (!docId || !authJwt) return
    setPatientMatchLoading(true)
    setPatientMatchError('')
    setPatientMatchDetermination(null)
    setPatientMatchExplanation(null)
    setPatientMatchConfidence(null)
    setPatientMismatch(false)

    try {
      await jwtPostFetcher(authJwt)(`/api/eve/documents/${docId}/patient-match`)

      const waitForNextPollTick = () => new Promise((resolve) => {
        setTimeout(resolve, EVE_POLL_INTERVAL_MS)
      })

      for (let attempt = 0; attempt <= EVE_MAX_POLL_ATTEMPTS; attempt += 1) {
        if (isCancelledRef.current) return
        const response = await jwtGetFetcher(authJwt)(`/api/eve/documents/${docId}/patient-match`)
        const status = normalizeStatus(response?.status || response?.state)

        if (EVE_TERMINAL_SUCCESS.includes(status)) {
          const pmDetermination = getPatientMatchDetermination(response)
          const isNoMatch = pmDetermination === 'NO_MATCH'
          setPatientMatchDetermination(pmDetermination)
          setPatientMismatch(isNoMatch || getPatientMismatch(response))
          setPatientMatchExplanation(response?.result?.explanation ?? response?.explanation ?? null)
          setPatientMatchConfidence(toPercentScore(response?.result?.confidenceScore ?? response?.confidenceScore))
          return
        }

        if (EVE_TERMINAL_ERROR.includes(status)) {
          throw new Error(response?.message || response?.error || 'Error loading patient match')
        }

        if (attempt >= EVE_MAX_POLL_ATTEMPTS) {
          throw new Error('Timed out while waiting for patient match.')
        }

        await waitForNextPollTick()
      }
    } catch (error) {
      if (!isCancelledRef.current) {
        setPatientMatchError(error?.message || 'Error loading patient match')
      }
    } finally {
      setPatientMatchLoading(false)
    }
  }, [resolvedEveDocumentId, authJwt])

  // Retry only relevancy
  const retryRelevancy = useCallback(async () => {
    const docId = resolvedEveDocumentId
    if (!docId || !authJwt) return
    setRelevancyLoading(true)
    setRelevancyError('')
    setRelevanceScore(null)
    setRelevancyClassification(null)
    setRelevancyExplanation(null)

    try {
      await jwtPostFetcher(authJwt)(`/api/eve/documents/${docId}/relevancy`)

      const waitForNextPollTick = () => new Promise((resolve) => {
        setTimeout(resolve, EVE_POLL_INTERVAL_MS)
      })

      for (let attempt = 0; attempt <= EVE_MAX_POLL_ATTEMPTS; attempt += 1) {
        if (isCancelledRef.current) return
        const response = await jwtGetFetcher(authJwt)(`/api/eve/documents/${docId}/relevancy`)
        const status = normalizeStatus(response?.status || response?.state)

        if (EVE_TERMINAL_SUCCESS.includes(status)) {
          setRelevanceScore(getRelevancyScore(response))
          setRelevancyClassification(getRelevancyClassification(response))
          setRelevancyExplanation(getRelevancyExplanation(response))
          return
        }

        if (EVE_TERMINAL_ERROR.includes(status)) {
          throw new Error(response?.message || response?.error || 'Error loading relevancy')
        }

        if (attempt >= EVE_MAX_POLL_ATTEMPTS) {
          throw new Error('Timed out while waiting for relevancy.')
        }

        await waitForNextPollTick()
      }
    } catch (error) {
      if (!isCancelledRef.current) {
        setRelevancyError(error?.message || 'Error loading relevancy')
      }
    } finally {
      setRelevancyLoading(false)
    }
  }, [resolvedEveDocumentId, authJwt])

  // Retry both patient-match and relevancy together
  const retryBothFeatures = useCallback(async () => {
    await Promise.all([retryPatientMatch(), retryRelevancy()])
  }, [retryPatientMatch, retryRelevancy])

  const submitFeatureFeedback = useCallback(async ({feature, rating, explanation = null, comment = ''}) => {
    const docId = resolvedEveDocumentId
    if (!docId || !authJwt || !feature || !rating) return false

    const payload = {
      feature,
      rating,
      isHelpful: rating === 'up',
      explanation,
      comment,
      message: comment,
    }

    const candidateEndpoints = [
      `/api/eve/documents/${docId}/${feature}/feedback`,
      `/api/eve/documents/${docId}/feedback`,
    ]

    for (const endpoint of candidateEndpoints) {
      try {
        await jwtPostFetcher(authJwt)(endpoint, payload)
        return true
      } catch (error) {
        // Try the next endpoint variant for backend compatibility.
      }
    }

    return false
  }, [resolvedEveDocumentId, authJwt])

  useEffect(() => {
    let isCancelled = false
    isCancelledRef.current = false

    const resetState = () => {
      setAiState('idle')
      setSummaryParagraphs([])
      setSummaryData(null)
      setSummaryErrorMessage('')
      setResolvedEveDocumentId(null)
      eveDocumentIdRef.current = null
      setRelevanceScore(null)
      setPatientMismatch(false)
      setRelevancyClassification(null)
      setRelevancyExplanation(null)
      setPatientMatchDetermination(null)
      setPatientMatchExplanation(null)
      setPatientMatchConfidence(null)
      setEveStatus('')
      setAiErrorMessage('')
      setPatientMatchError('')
      setRelevancyError('')
      setFeaturesCancelled(false)
      featuresCancelledRef.current = false
      setFeaturesLoading(false)
      setPatientMatchLoading(false)
      setRelevancyLoading(false)
      setSummaryCancelled(false)
      setCanCancel(false)
      setCanRetry(false)
      setShowFeatureCancelLink(false)
      lastStatusRef.current = null
    }

    if (!isExpanded) {
      clearPolling()
      resetState()
      return () => {
        isCancelled = true
        isCancelledRef.current = true
      }
    }

    if (!requestId || !recordId || !authJwt) {
      setAiState('error')
      setAiErrorMessage('Missing request or record information.')
      return () => {
        isCancelled = true
        isCancelledRef.current = true
      }
    }

    const run = async () => {
      try {
        setAiState('loading')
        setAiErrorMessage('')
        setPatientMatchError('')
        setRelevancyError('')
        setCanCancel(false)
        setFeaturesLoading(false)
        setPatientMatchLoading(false)
        setRelevancyLoading(false)
        setShowFeatureCancelLink(false)

        const waitForNextPollTick = () => new Promise((resolve) => {
          pollTimeoutRef.current = setTimeout(resolve, EVE_POLL_INTERVAL_MS)
        })

        const pollFeatureStatus = async ({endpoint, shouldTriggerInitially, featureLabel}) => {
          if (shouldTriggerInitially) {
            await jwtPostFetcher(authJwt)(endpoint)
          }

          for (let attempt = 0; attempt <= EVE_MAX_POLL_ATTEMPTS; attempt += 1) {
            if (isCancelled || featuresCancelledRef.current) return null

            const featureResponse = await jwtGetFetcher(authJwt)(endpoint)
            const featureStatus = normalizeStatus(featureResponse?.status || featureResponse?.state)

            if (EVE_TERMINAL_SUCCESS.includes(featureStatus)) {
              return featureResponse
            }

            if (EVE_TERMINAL_ERROR.includes(featureStatus)) {
              throw new Error(featureResponse?.message || featureResponse?.error || `Error loading ${featureLabel}`)
            }

            if (attempt >= EVE_MAX_POLL_ATTEMPTS) {
              throw new Error(`Timed out while waiting for ${featureLabel}.`)
            }

            await waitForNextPollTick()
          }

          throw new Error(`Error loading ${featureLabel}`)
        }

        // If this is a retry (retryTrigger > 0) and we already have an eveDocumentId
        let eveDocumentId = eveDocumentIdRef.current
        if (retryTrigger > 0 && eveDocumentId) {
          if (EVE_RETRY_FULL_STATUSES.includes(lastStatusRef.current)) {
            // Status was in early pipeline phases — use /retry which re-ingests the document
            const retryResponse = await jwtPostFetcher(authJwt)(`/api/eve/documents/${eveDocumentId}/retry`)
            const newEveDocumentId = getEveDocumentId(retryResponse)
            if (newEveDocumentId && newEveDocumentId !== eveDocumentId) {
              eveDocumentId = newEveDocumentId
              eveDocumentIdRef.current = newEveDocumentId
              if (!isCancelled) {
                setResolvedEveDocumentId(newEveDocumentId)
              }
            }
          } else {
            // Document is already ingested — only re-trigger summarization
            await jwtPostFetcher(authJwt)(`/api/eve/documents/${eveDocumentId}/summarize`)
          }
        } else {
          const parsedRecordId = Number(recordId)
          let ingestResponse
          try {
            ingestResponse = await jwtPostFetcher(authJwt)(
              `/api/eve/ingest?requestId=${requestId}`,
              {recordId: Number.isFinite(parsedRecordId) ? parsedRecordId : recordId}
            )
          } catch (ingestError) {
            if (!isCancelled) setCanRetry(true)
            throw ingestError
          }
          setEveStatus(ingestResponse?.status || 'RECEIVED')

          eveDocumentId = getEveDocumentId(ingestResponse)
          if (!eveDocumentId) {
            throw new Error('EVE ingest response did not include eveDocumentId.')
          }
          if (!isCancelled) {
            setResolvedEveDocumentId(eveDocumentId)
            eveDocumentIdRef.current = eveDocumentId
          }
        }

        const initialStatusResponse = await jwtGetFetcher(authJwt)(`/api/eve/documents/${eveDocumentId}/status`)
        const shouldTriggerPatientMatch = !Boolean(initialStatusResponse?.patientMatchCompletedAt)
        const shouldTriggerRelevancy = !Boolean(initialStatusResponse?.relevancyCompletedAt)
        let patientMatchTriggered = false
        let relevancyTriggered = false
        let patientMatchPromise = null
        let relevancyPromise = null

        const startFeaturePolling = () => {
          if (!patientMatchPromise) {
            setPatientMatchLoading(true)
            // Start feature cancel timer (right side) when features begin polling
            featureCancelTimerRef.current = setTimeout(() => {
              if (!isCancelled && !featuresCancelledRef.current) setShowFeatureCancelLink(true)
            }, getAiIngestionTimeout())

            patientMatchPromise = pollFeatureStatus({
              endpoint: `/api/eve/documents/${eveDocumentId}/patient-match`,
              shouldTriggerInitially: shouldTriggerPatientMatch && !patientMatchTriggered,
              featureLabel: 'patient match status',
            })
              .then((response) => {
                if (!isCancelled) {
                  const pmDetermination = getPatientMatchDetermination(response)
                  const isNoMatch = pmDetermination === 'NO_MATCH'
                  setPatientMatchDetermination(pmDetermination)
                  setPatientMismatch(isNoMatch || getPatientMismatch(response))
                  setPatientMatchExplanation(response?.result?.explanation ?? response?.explanation ?? null)
                  setPatientMatchConfidence(toPercentScore(response?.result?.confidenceScore ?? response?.confidenceScore))
                  setPatientMatchError('')
                }
                if (!isCancelled) setPatientMatchLoading(false)
                return {ok: true, response}
              })
              .catch((error) => {
                if (!isCancelled) {
                  setPatientMatchLoading(false)
                  setPatientMatchError(error?.message || 'Error loading patient match')
                }
                return {ok: false, error}
              })
          }

          if (!relevancyPromise) {
            setRelevancyLoading(true)
            relevancyPromise = pollFeatureStatus({
              endpoint: `/api/eve/documents/${eveDocumentId}/relevancy`,
              shouldTriggerInitially: shouldTriggerRelevancy && !relevancyTriggered,
              featureLabel: 'relevancy status',
            })
              .then((response) => {
                if (!isCancelled) {
                  setRelevanceScore(getRelevancyScore(response))
                  setRelevancyClassification(getRelevancyClassification(response))
                  setRelevancyExplanation(getRelevancyExplanation(response))
                  setRelevancyError('')
                }
                if (!isCancelled) setRelevancyLoading(false)
                return {ok: true, response}
              })
              .catch((error) => {
                if (!isCancelled) {
                  setRelevancyLoading(false)
                  setRelevancyError(error?.message || 'Error loading relevancy')
                }
                return {ok: false, error}
              })
          }
        }

        const statusPollingStartedAt = Date.now()

        const pollStatus = async () => {
          if (isCancelled) return

          const statusResponse = await jwtGetFetcher(authJwt)(`/api/eve/documents/${eveDocumentId}/status`)
          setEveStatus(statusResponse?.status || statusResponse?.state || '')
          const status = normalizeStatus(statusResponse?.status || statusResponse?.state)
          setCanCancel(statusResponse?.canCancel || false)
          setCanRetry(statusResponse?.canRetry || false)
          lastStatusRef.current = status

          // Start feature polling as soon as EVE reaches ingest/summarize phase.
          if (status === 'ingested' || status === 'summarizing') {
            startFeaturePolling()
          }

          if (EVE_TERMINAL_SUCCESS.includes(status)) {
            // Ensure feature polling has started even if status jumps quickly to COMPLETED.
            startFeaturePolling()
            setFeaturesLoading(true)

            // Fetch summary immediately — show it as soon as it's ready without waiting for features.
            const summaryResult = await jwtGetFetcher(authJwt)(`/api/eve/documents/${eveDocumentId}/summary`)
              .then((response) => ({ok: true, response}))
              .catch((error) => ({ok: false, error}))

            if (!isCancelled) {
              const summaryResponse = summaryResult?.ok ? summaryResult.response : null
              const summaryError = summaryResult?.ok
                ? ''
                : (summaryResult?.error?.message || 'The AI summary could not be loaded at this time. Please try again later.')

              setSummaryParagraphs(getSummaryParagraphs(summaryResponse))
              setSummaryData(summaryResponse?.result || summaryResponse || null)
              setSummaryErrorMessage(summaryError)
              // Seed patient mismatch from summary/status; patientMatchPromise will refine it when it settles.
              setPatientMismatch(getPatientMismatch(summaryResponse) || getPatientMismatch(statusResponse))
              setAiState('done')
            }

            // Features continue in background; their .then() callbacks already update state.
            // Clear the feature loading indicator once both settle.
            Promise.all([patientMatchPromise, relevancyPromise]).finally(() => {
              if (!isCancelled) {
                clearTimeout(featureCancelTimerRef.current)
                setShowFeatureCancelLink(false)
                setFeaturesLoading(false)
              }
            })

            return
          }

          if (EVE_TERMINAL_ERROR.includes(status)) {
            const responseError =
              status === 'not_implemented'
                ? 'AI summary is not available for this document type yet.'
                : statusResponse?.message || statusResponse?.error || 'Error loading AI summary'
            throw new Error(responseError)
          }

          const elapsedMs = Date.now() - statusPollingStartedAt
          const nextPollInMs = getSummaryStatusPollIntervalMs(elapsedMs)

          pollTimeoutRef.current = setTimeout(() => {
            pollStatus().catch((error) => {
              if (!isCancelled) {
                setAiState('error')
                setAiErrorMessage(error?.message || 'Error loading AI summary')
              }
            })
          }, nextPollInMs)
        }

        await pollStatus()
      } catch (error) {
        if (!isCancelled) {
          setAiState('error')
          setAiErrorMessage(error?.message || 'Error loading AI summary')
        }
      }
    }

    run()

    return () => {
      isCancelled = true
      isCancelledRef.current = true
      clearPolling()
    }
  }, [isExpanded, requestId, recordId, authJwt, clearPolling, retryTrigger])

  return {
    aiState,
    relevanceScore,
    patientMismatch,
    summaryParagraphs,
    summaryData,
    summaryErrorMessage,
    aiErrorMessage,
    eveStatus,
    relevancyClassification,
    relevancyExplanation,
    patientMatchDetermination,
    patientMatchExplanation,
    patientMatchConfidence,
    eveDocumentId: resolvedEveDocumentId,
    patientMatchError,
    relevancyError,
    retryAll,
    retryPatientMatch,
    retryRelevancy,
    retryBothFeatures,
    submitFeatureFeedback,
    cancelSummary,
    fakeCancelSummary,
    cancelFeatures,
    loadFeatures,
    canCancel,
    canRetry,
    showFeatureCancelLink,
    featuresCancelled,
    featuresLoading,
    patientMatchLoading,
    relevancyLoading,
    summaryCancelled,
  }
}
